'use client';

import { Box, LayoutGrid } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useGraphics } from '../settings/GraphicsContext';

/**
 * 3D vs Lite 2D board picker. Used by the mobile tutorial's view-mode
 * decision card. (The first-run player tour no longer shows a picker —
 * the board defaults to 2D and 3D is an opt-in from the graphics menu.)
 *
 * - 3D tile  → `setAuto()` restores the detected level (clears any sticky
 *              lite override), so the user ends up on the best-for-device
 *              3D preset.
 * - 2D tile  → `setLevel('lite')` pins the 2D fallback — the same flag
 *              the in-game graphics menu and `useTwoDFallback` watch.
 *
 * Selection is reflected back from `level` + `auto` so the active tile
 * is highlighted and persists through `GRAPHICS_STORAGE_KEY`.
 */
export function ViewModePick() {
  const { level, auto, setLevel, setAuto } = useGraphics();
  const liteActive = level === 'lite';
  const threeDActive = !liteActive;

  return (
    <div
      role="radiogroup"
      aria-label="Board view mode"
      className="mt-2 grid grid-cols-2 gap-2"
    >
      <ViewTile
        icon={<Box className="h-4 w-4" />}
        label="3D cinematic"
        sub={auto && threeDActive ? 'Auto · recommended' : 'Full scene'}
        active={threeDActive}
        onClick={setAuto}
      />
      <ViewTile
        icon={<LayoutGrid className="h-4 w-4" />}
        label="Lite 2D"
        sub="Top-down · lighter"
        active={liteActive}
        onClick={() => setLevel('lite')}
      />
    </div>
  );
}

interface ViewTileProps {
  icon: React.ReactNode;
  label: string;
  sub: string;
  active: boolean;
  onClick: () => void;
}

function ViewTile({ icon, label, sub, active, onClick }: ViewTileProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-1.5 rounded-[4px] border px-3 py-2.5 text-left transition',
        active
          ? 'border-amber-400/60 bg-amber-400/10 shadow-[inset_0_0_0_1px_rgba(255,200,100,0.25)]'
          : 'border-amber-400/15 bg-black/30 hover:border-amber-400/35 hover:bg-amber-400/5',
      )}
    >
      <span
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-[3px]',
          active ? 'bg-amber-400/20 text-amber-100' : 'bg-amber-100/5 text-amber-100/70',
        )}
      >
        {icon}
      </span>
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-50">
        {label}
      </span>
      <span className="text-[10px] leading-snug text-amber-100/55">{sub}</span>
    </button>
  );
}
